// "Invoices" card of a ShareJet booking: pre-auth and final invoices with their download.
import { Button, Card, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

import type { AdminBookingDetail, AdminBookingInvoice } from '../../api/types';
import { gbp, dateTime } from '../../data/format';

const { Text } = Typography;

export function BookingInvoicesCard({ b }: { b: AdminBookingDetail }) {
  return (
    <Card title="Invoices">
      <Table<AdminBookingInvoice>
        rowKey="id"
        dataSource={b.invoices}
        pagination={false}
        size="small"
        locale={{ emptyText: 'No invoices' }}
        columns={[
          {
            title: 'Kind',
            dataIndex: 'kind',
            key: 'kind',
            // SHAR-1098 — a released pre-auth says so, as it does in the apps:
            // the hold was let go (expired, user cancelled, …), no money moved.
            // SHAR-1097 — and a captured one says "Captured": its money is the
            // charge on the Final invoice.
            render: (k: AdminBookingInvoice['kind'], r: AdminBookingInvoice) =>
              k === 'preauth' ? (
                <Space size={6}>
                  Pre-auth
                  {r.releasedAt ? (
                    <Tooltip
                      title={`Released ${dateTime(r.releasedAt)}${r.releaseReason ? ` — ${r.releaseReason}` : ''}`}
                    >
                      <Tag color="default">Released</Tag>
                    </Tooltip>
                  ) : r.captured ? (
                    <Tag color="green">Captured</Tag>
                  ) : null}
                </Space>
              ) : (
                'Final'
              ),
          },
          { title: 'Number', dataIndex: 'number', key: 'number' },
          {
            // SHAR-1011 — whose invoice this is. A ShareJet booking can be paid
            // for by several people, and the block listed their invoices with
            // nothing to tell them apart.
            title: 'User',
            key: 'user',
            ellipsis: { showTitle: true },
            render: (_: unknown, r: AdminBookingInvoice) =>
              r.userName || r.userEmail
                ? `${r.userName ?? '—'}${r.userEmail ? ` (${r.userEmail})` : ''}`
                : '—',
          },
          {
            title: 'Amount',
            dataIndex: 'amountGbp',
            key: 'amountGbp',
            render: (v: number) => `${gbp(v)}`,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (v: string) => dateTime(v),
          },
          {
            // SHAR-1009 — the same download the charter blocks have. The link
            // is signed and carries its own credential, so a plain href works.
            title: '',
            key: 'download',
            width: 140,
            render: (_: unknown, r: AdminBookingInvoice) =>
              r.downloadUrl ? (
                <Button type="link" size="small" icon={<DownloadOutlined />} href={r.downloadUrl} target="_blank" rel="noreferrer">
                  Download
                </Button>
              ) : (
                <Text type="secondary">—</Text>
              ),
          },
        ]}
      />
    </Card>
  );
}
